import { supabase } from "@/lib/supabase";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim() ?? "";
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() ?? "";

async function authToken(): Promise<string> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Not signed in");
  return token;
}

function restUrl(table: string, params?: Record<string, string>): string {
  const qs = params ? new URLSearchParams(params).toString() : "";
  return `${supabaseUrl}/rest/v1/${table}${qs ? `?${qs}` : ""}`;
}

async function restFetch<T>(
  url: string,
  init: RequestInit & { prefer?: string } = {},
): Promise<T> {
  const token = await authToken();
  const headers: Record<string, string> = {
    apikey: anonKey,
    Authorization: `Bearer ${token}`,
    ...(init.headers as Record<string, string>),
  };
  if (init.prefer) headers.Prefer = init.prefer;

  const res = await fetch(url, { ...init, headers });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`${res.status} ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export async function restGet<T = unknown>(
  table: string,
  params: Record<string, string>,
): Promise<T> {
  return restFetch<T>(restUrl(table, params), {
    headers: { Accept: "application/json" },
  });
}

/** Exact row count via PostgREST Content-Range (Prefer: count=exact). */
export async function restCount(
  table: string,
  params: Record<string, string>,
): Promise<number> {
  const token = await authToken();
  const qs = new URLSearchParams({
    select: "id",
    limit: "0",
    ...params,
  }).toString();
  const res = await fetch(`${supabaseUrl}/rest/v1/${table}?${qs}`, {
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${token}`,
      Prefer: "count=exact",
      Accept: "application/json",
    },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`${res.status} ${detail}`);
  }
  const range = res.headers.get("Content-Range");
  if (!range) return 0;
  const match = range.match(/\/(\d+)$/);
  return match ? Number.parseInt(match[1], 10) : 0;
}

export async function restPost<T = unknown>(
  table: string,
  body: Record<string, unknown>,
  params?: Record<string, string>,
  prefer = "return=minimal",
): Promise<T> {
  return restFetch<T>(restUrl(table, params), {
    method: "POST",
    prefer,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
}

export async function restPatch<T = unknown>(
  table: string,
  body: Record<string, unknown>,
  params: Record<string, string>,
  prefer = "return=minimal",
): Promise<T> {
  return restFetch<T>(restUrl(table, params), {
    method: "PATCH",
    prefer,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
}

export async function restDelete(
  table: string,
  params: Record<string, string>,
): Promise<void> {
  await restFetch(restUrl(table, params), { method: "DELETE" });
}

export async function restRpc<T = unknown>(
  fn: string,
  args: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

export async function edgePost<T = unknown>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Not signed in");

  const res = await fetch(`${supabaseUrl}/functions/v1/${path}`, {
    method: "POST",
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`${path}: ${res.status} ${detail}`);
  }
  return res.json() as Promise<T>;
}

export async function edgeGet<T = unknown>(
  path: string,
  params: Record<string, string>,
): Promise<T> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Not signed in");

  const qs = new URLSearchParams(params).toString();
  const res = await fetch(
    `${supabaseUrl}/functions/v1/${path}${qs ? `?${qs}` : ""}`,
    {
      headers: {
        apikey: anonKey,
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`${path}: ${res.status} ${detail}`);
  }
  return res.json() as Promise<T>;
}
